import {
  DEFAULT_RESOLUTION_SORT,
  RESOLUTIONS_TABLE_HEADER
} from '@bo-constants/resolutions/resolutionsList';
import {
  Table,
  TableBodyInfiniteScroll,
  TableHeader
} from '@library/components/table';
import { useCallback, useMemo, useState } from 'react';
import { Loading } from '@library/components/Loading';
import { NoResults } from '@library/components/NoResults';
import type { ResolutionSort } from '@library/types/resolutions/resolutionsList';
import { ResolutionsRow } from '@bo-components/resolutions/resolutionsList';
import { useResolutions } from '@bo-hooks/resolutions/resolutionsList/useResolutions';

export interface ResolutionsTableProps {
  onPageChange: () => void;
}

export const ResolutionsTable = () => {
  const [sort, setSort] = useState<ResolutionSort>(DEFAULT_RESOLUTION_SORT);

  const {
    currentPage,
    isFetching: isResolutionsFetching,
    onClickResolution,
    onPageChange,
    onResetPageChange,
    resolutions,
    totalPages
  } = useResolutions({ resolutionSort: sort });

  const handleSort = useCallback(
    (sortColumn: ResolutionSort) => {
      onResetPageChange();
      setSort(sortColumn);
    },
    [onResetPageChange, setSort]
  );

  const resolutionsCount = Object.values(resolutions).length;

  const infiniteScrollProps = useMemo(
    () => ({
      dataLength: resolutionsCount,
      hasMore: totalPages > currentPage,
      loader: <p>Loading...</p>,
      next: onPageChange
    }),
    [resolutionsCount, currentPage, onPageChange, totalPages]
  );

  if (!resolutionsCount) {
    return (
      <NoResults
        className="m-0"
        dataTestId="no-resolutions"
        label="Resolutions not found"
      />
    );
  }

  if (isResolutionsFetching) {
    return <Loading loadingMessage="Loading resolution list." />;
  }

  return (
    <Table
      className="border-soFresh-140 overflow-x-auto rounded-lg border p-0"
      dataTestId="resolutions-list"
    >
      <TableHeader
        className="pl-4"
        headers={RESOLUTIONS_TABLE_HEADER}
        sort={sort}
        onSort={handleSort}
      />
      <TableBodyInfiniteScroll
        bodyHeight="[calc(100vh-22rem)]"
        bodyMaxHeight="max-h-[calc(100vh-22rem)]"
        infiniteScrollProps={infiniteScrollProps}
        scrollId="ResolutionsListTableBody"
      >
        {Object.values(resolutions).map((resolution) => (
          <ResolutionsRow
            key={resolution.id}
            resolution={resolution}
            onClickResolution={onClickResolution}
          />
        ))}
      </TableBodyInfiniteScroll>
    </Table>
  );
};
